import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { historiasClinicasAPI, comprobantesAtencionAPI } from '../services/api';
import ModalCobrarAtencion from '../components/ModalCobrarAtencion';

const ITEMS_PER_PAGE = 10;

const estadoBadge = (estado) => {
  switch (estado) {
    case 'completada': return 'bg-success';
    case 'borrador': return 'bg-warning text-dark';
    case 'anulada': return 'bg-danger';
    default: return 'bg-secondary';
  }
};

const estadoLabel = (estado) => {
  switch (estado) {
    case 'completada': return 'Completada';
    case 'borrador': return 'Borrador';
    case 'anulada': return 'Anulada';
    default: return estado || 'Sin estado';
  }
};

const truncate = (text, max = 40) => {
  if (!text) return '—';
  return text.length > max ? text.substring(0, max) + '...' : text;
};

const HistoriasClinicasList = () => {
  const navigate = useNavigate();

  // Data
  const [historias, setHistorias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalItems, setTotalItems] = useState(0);

  // Pagination
  const [page, setPage] = useState(1);

  // Filters
  const [filtros, setFiltros] = useState({
    fecha_desde: '',
    fecha_hasta: '',
    busqueda: '',
    optometrista_id: '',
    estado: ''
  });

  // Summary counts
  const [resumen, setResumen] = useState({ total: 0, completadas: 0, borradores: 0 });

  // Export PDF modal state
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportCliente, setExportCliente] = useState(null);
  const [exportHCs, setExportHCs] = useState([]);
  const [selectedHCIds, setSelectedHCIds] = useState(new Set());
  const [exporting, setExporting] = useState(false);
  const [loadingExportHCs, setLoadingExportHCs] = useState(false);

  // Modal cobrar atención
  const [showCobrarModal, setShowCobrarModal] = useState(false);
  const [cobrarContexto, setCobrarContexto] = useState({ cliente: null, hcId: null });

  const abrirCobro = (hc) => {
    // FIX: parseo robusto del nombre. Si hay 1 solo token -> va como nombres
    // Si hay 2+ tokens -> ultimo como apellidos, resto como nombres.
    let nombres = '', apellidos = '';
    const full = (hc?.cliente_nombre || '').trim();
    if (full) {
      const partes = full.split(/\s+/);
      if (partes.length === 1) {
        nombres = partes[0];
      } else {
        apellidos = partes[partes.length - 1];
        nombres = partes.slice(0, -1).join(' ');
      }
    }
    const cliente = hc && hc.cliente_id ? {
      id: hc.cliente_id,
      nombres,
      apellidos,
      identificacion: hc.cliente_ci,
      email: hc.cliente_email,
      celular: hc.cliente_celular
    } : null;
    setCobrarContexto({ cliente, hcId: hc?.id || null });
    setShowCobrarModal(true);
  };

  const abrirComprobantePDF = async (comprobanteId) => {
    if (!comprobanteId) { toast.warn('Comprobante no disponible'); return; }
    try {
      const res = await comprobantesAtencionAPI.descargarPDF(comprobanteId);
      const url = URL.createObjectURL(res.data);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000); // libera memoria
    } catch (e) {
      toast.error('Error al abrir comprobante');
    }
  };

  // ─── Cargar datos ───
  const cargarHistorias = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        limit: ITEMS_PER_PAGE
      };
      if (filtros.fecha_desde) params.fecha_desde = filtros.fecha_desde;
      if (filtros.fecha_hasta) params.fecha_hasta = filtros.fecha_hasta;
      if (filtros.estado) params.estado = filtros.estado;
      if (filtros.optometrista_id) params.optometrista_id = filtros.optometrista_id;

      const res = await historiasClinicasAPI.getAll(params);

      if (res.data?.success) {
        let data = res.data.data || [];

        // Client-side search filter (nombre/CI)
        if (filtros.busqueda.trim()) {
          const term = filtros.busqueda.toLowerCase().trim();
          data = data.filter(h =>
            (h.cliente_nombre && h.cliente_nombre.toLowerCase().includes(term)) ||
            (h.cliente_ci && h.cliente_ci.toLowerCase().includes(term))
          );
        }

        setHistorias(data);
        // Since backend doesn't return total count, estimate from data length
        // If we got a full page, there might be more
        setTotalItems(data.length);

        // Calculate summary from current data set
        const completadas = data.filter(h => h.estado === 'completada').length;
        const borradores = data.filter(h => h.estado === 'borrador').length;
        setResumen({
          total: data.length,
          completadas,
          borradores
        });
      }
    } catch (error) {
      console.error('Error cargando historias clínicas:', error);
      toast.error('Error al cargar historias clínicas');
    } finally {
      setLoading(false);
    }
  }, [page, filtros]);

  useEffect(() => {
    cargarHistorias();
  }, [cargarHistorias]);

  // Reset page when filters change
  const handleFiltroChange = (field, value) => {
    setFiltros(prev => ({ ...prev, [field]: value }));
    setPage(1);
  };

  const limpiarFiltros = () => {
    setFiltros({ fecha_desde: '', fecha_hasta: '', busqueda: '', optometrista_id: '', estado: '' });
    setPage(1);
  };

  // ─── Export PDF helpers ───
  const getUniquePacientes = useCallback(() => {
    const map = new Map();
    historias.forEach(hc => {
      if (hc.cliente_id && !map.has(hc.cliente_id)) {
        map.set(hc.cliente_id, {
          id: hc.cliente_id,
          nombre: hc.cliente_nombre || 'Sin nombre',
          ci: hc.cliente_ci || ''
        });
      }
    });
    return Array.from(map.values());
  }, [historias]);

  const openExportModal = (clienteId = null) => {
    setShowExportModal(true);
    setExportHCs([]);
    setSelectedHCIds(new Set());
    if (clienteId) {
      const paciente = historias.find(h => h.cliente_id === clienteId);
      if (paciente) {
        handleSelectPaciente({
          id: paciente.cliente_id,
          nombre: paciente.cliente_nombre,
          ci: paciente.cliente_ci
        });
      }
    } else {
      setExportCliente(null);
    }
  };

  const closeExportModal = () => {
    setShowExportModal(false);
    setExportCliente(null);
    setExportHCs([]);
    setSelectedHCIds(new Set());
    setExporting(false);
  };

  const handleSelectPaciente = async (paciente) => {
    setExportCliente(paciente);
    setLoadingExportHCs(true);
    try {
      const res = await historiasClinicasAPI.getAll({ cliente_id: paciente.id, limit: 100 });
      if (res.data?.success) {
        const hcs = res.data.data || [];
        setExportHCs(hcs);
        setSelectedHCIds(new Set(hcs.map(h => h.id)));
      }
    } catch (err) {
      console.error('Error cargando HCs del paciente:', err);
      toast.error('Error al cargar historias del paciente');
    } finally {
      setLoadingExportHCs(false);
    }
  };

  const toggleHCSelection = (id) => {
    setSelectedHCIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAllHCs = () => {
    if (selectedHCIds.size === exportHCs.length) {
      setSelectedHCIds(new Set());
    } else {
      setSelectedHCIds(new Set(exportHCs.map(h => h.id)));
    }
  };

  const handleExportPdf = async (mode = 'download') => {
    if (selectedHCIds.size === 0) {
      toast.warning('Seleccione al menos una historia cl\u00ednica');
      return;
    }
    setExporting(true);
    try {
      const res = await historiasClinicasAPI.exportPdf({
        cliente_id: exportCliente.id,
        historia_ids: Array.from(selectedHCIds)
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);

      if (mode === 'download') {
        const link = document.createElement('a');
        link.href = url;
        link.download = `ficha_${exportCliente.nombre.replace(/\s+/g, '_')}_${new Date().toISOString().slice(0, 10)}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success('PDF descargado correctamente');
      } else {
        const printWindow = window.open(url, '_blank');
        if (printWindow) {
          printWindow.addEventListener('load', () => {
            printWindow.print();
          });
        }
      }

      window.URL.revokeObjectURL(url);
      closeExportModal();
    } catch (err) {
      console.error('Error exportando PDF:', err);
      toast.error('Error al generar el PDF');
    } finally {
      setExporting(false);
    }
  };

  // Pagination calculations
  const totalPages = Math.max(1, Math.ceil(totalItems / ITEMS_PER_PAGE));
  const startItem = totalItems === 0 ? 0 : (page - 1) * ITEMS_PER_PAGE + 1;
  const endItem = Math.min(page * ITEMS_PER_PAGE, totalItems);

  const formatFecha = (fecha) => {
    if (!fecha) return '—';
    try {
      return new Date(fecha).toLocaleDateString('es-EC', {
        day: '2-digit', month: '2-digit', year: 'numeric'
      });
    } catch {
      return fecha;
    }
  };

  // ─── Render ───
  return (
    <div className="container-fluid py-4">
      {/* Header */}
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4">
        <div>
          <h2 className="mb-1">
            <i className="bi bi-journal-medical me-2"></i>
            Historias Cl&iacute;nicas
          </h2>
          <p className="text-muted mb-0">Listado y gesti&oacute;n de historias cl&iacute;nicas</p>
        </div>
        <div className="d-flex gap-2 mt-2 mt-md-0 flex-wrap">
          <button
            className="btn btn-outline-secondary"
            onClick={() => abrirCobro(null)}
            title="Cobrar atenci&oacute;n sin HC (walk-in)"
          >
            <i className="bi bi-cash-stack me-1"></i>
            Cobro r&aacute;pido
          </button>
          <button
            className="btn btn-outline-danger"
            onClick={() => openExportModal()}
            disabled={historias.length === 0}
          >
            <i className="bi bi-file-earmark-pdf me-1"></i>
            Exportar Ficha
          </button>
          <button
            className="btn btn-primary"
            onClick={() => navigate('/historia-clinica/nueva')}
          >
            <i className="bi bi-plus-lg me-1"></i>
            Nueva Historia Cl&iacute;nica
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="row g-3 mb-4">
        <div className="col-6 col-md-4">
          <div className="card border-0 shadow-sm">
            <div className="card-body text-center py-3">
              <div className="text-muted small">Total HC</div>
              <div className="fs-3 fw-bold text-primary">{resumen.total}</div>
            </div>
          </div>
        </div>
        <div className="col-6 col-md-4">
          <div className="card border-0 shadow-sm">
            <div className="card-body text-center py-3">
              <div className="text-muted small">Completadas</div>
              <div className="fs-3 fw-bold text-success">{resumen.completadas}</div>
            </div>
          </div>
        </div>
        <div className="col-12 col-md-4">
          <div className="card border-0 shadow-sm">
            <div className="card-body text-center py-3">
              <div className="text-muted small">Borradores</div>
              <div className="fs-3 fw-bold text-warning">{resumen.borradores}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body">
          <div className="row g-2 align-items-end">
            <div className="col-12 col-sm-6 col-lg-3">
              <label className="form-label small mb-1">Buscar paciente</label>
              <div className="input-group input-group-sm">
                <span className="input-group-text"><i className="bi bi-search"></i></span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Nombre o CI..."
                  value={filtros.busqueda}
                  onChange={e => handleFiltroChange('busqueda', e.target.value)}
                />
              </div>
            </div>
            <div className="col-6 col-sm-3 col-lg-2">
              <label className="form-label small mb-1">Desde</label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={filtros.fecha_desde}
                onChange={e => handleFiltroChange('fecha_desde', e.target.value)}
              />
            </div>
            <div className="col-6 col-sm-3 col-lg-2">
              <label className="form-label small mb-1">Hasta</label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={filtros.fecha_hasta}
                onChange={e => handleFiltroChange('fecha_hasta', e.target.value)}
              />
            </div>
            <div className="col-6 col-sm-3 col-lg-2">
              <label className="form-label small mb-1">Estado</label>
              <select
                className="form-select form-select-sm"
                value={filtros.estado}
                onChange={e => handleFiltroChange('estado', e.target.value)}
              >
                <option value="">Todos</option>
                <option value="completada">Completada</option>
                <option value="borrador">Borrador</option>
                <option value="anulada">Anulada</option>
              </select>
            </div>
            <div className="col-6 col-sm-3 col-lg-2">
              <label className="form-label small mb-1">&nbsp;</label>
              <button
                className="btn btn-outline-secondary btn-sm w-100"
                onClick={limpiarFiltros}
              >
                <i className="bi bi-x-lg me-1"></i>Limpiar
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Table / Cards */}
      <div className="card border-0 shadow-sm">
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Cargando...</span>
              </div>
              <p className="text-muted mt-2">Cargando historias cl&iacute;nicas...</p>
            </div>
          ) : historias.length === 0 ? (
            <div className="text-center py-5">
              <i className="bi bi-journal-medical text-muted" style={{ fontSize: '3rem' }}></i>
              <p className="text-muted mt-2">No se encontraron historias cl&iacute;nicas</p>
              <button
                className="btn btn-primary btn-sm"
                onClick={() => navigate('/historia-clinica/nueva')}
              >
                <i className="bi bi-plus-lg me-1"></i>Crear primera historia cl&iacute;nica
              </button>
            </div>
          ) : (
            <>
              {/* Desktop/Tablet Table */}
              <div className="d-none d-md-block table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th className="ps-3">Fecha</th>
                      <th>C&oacute;digo</th>
                      <th>Paciente</th>
                      <th className="d-none d-lg-table-cell">Optometrista</th>
                      <th className="d-none d-lg-table-cell">Motivo Consulta</th>
                      <th className="d-none d-xl-table-cell">Diagn&oacute;stico</th>
                      <th className="text-center">Estado</th>
                      <th className="text-center">Cobro</th>
                      <th className="text-end pe-3">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historias.map(hc => (
                      <tr key={hc.id}>
                        <td className="ps-3">
                          <small>{formatFecha(hc.fecha_consulta)}</small>
                        </td>
                        <td>
                          <span className="fw-semibold text-primary">{hc.codigo || '—'}</span>
                        </td>
                        <td>
                          <div className="fw-semibold">{hc.cliente_nombre || '—'}</div>
                          <small className="text-muted">{hc.cliente_ci || ''}</small>
                        </td>
                        <td className="d-none d-lg-table-cell">
                          <small>{hc.optometrista || '—'}</small>
                        </td>
                        <td className="d-none d-lg-table-cell">
                          <small>{truncate(hc.motivo_consulta, 30)}</small>
                        </td>
                        <td className="d-none d-xl-table-cell">
                          <small className="text-muted">{truncate(hc.diagnostico, 35)}</small>
                        </td>
                        <td className="text-center">
                          <span className={`badge ${estadoBadge(hc.estado)}`}>
                            {estadoLabel(hc.estado)}
                          </span>
                        </td>
                        <td className="text-center">
                          {hc.comprobante_numero ? (
                            <button
                              className="btn btn-sm btn-link p-0 text-success"
                              onClick={() => abrirComprobantePDF(hc.comprobante_id)}
                              title={`Ver comprobante ${hc.comprobante_numero}`}
                            >
                              <i className="bi bi-check-circle-fill me-1"></i>
                              <small className="fw-bold">{hc.comprobante_numero}</small>
                            </button>
                          ) : hc.estado === 'completada' ? (
                            <button
                              className="btn btn-sm"
                              style={{ background: '#1B4F72', color: 'white', border: 'none' }}
                              onClick={() => abrirCobro(hc)}
                              title="Cobrar atenci&oacute;n"
                            >
                              <i className="bi bi-cash-stack me-1"></i>Cobrar
                            </button>
                          ) : (
                            <small className="text-muted">—</small>
                          )}
                        </td>
                        <td className="text-end pe-3">
                          <div className="btn-group btn-group-sm">
                            <button
                              className="btn btn-outline-primary"
                              title="Ver"
                              onClick={() => navigate(`/historia-clinica/${hc.id}`)}
                            >
                              <i className="bi bi-eye"></i>
                            </button>
                            <button
                              className="btn btn-outline-secondary"
                              title="Editar"
                              onClick={() => navigate(`/historia-clinica/${hc.id}`)}
                            >
                              <i className="bi bi-pencil"></i>
                            </button>
                            <button
                              className="btn btn-outline-success"
                              title="Nueva HC para este paciente"
                              onClick={() => navigate(`/historia-clinica/nueva?cliente=${hc.cliente_id}`)}
                            >
                              <i className="bi bi-plus-lg"></i>
                            </button>
                            <button
                              className="btn btn-outline-danger"
                              title="Exportar PDF"
                              onClick={() => openExportModal(hc.cliente_id)}
                            >
                              <i className="bi bi-file-earmark-pdf"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card View */}
              <div className="d-md-none p-3">
                {historias.map(hc => (
                  <div key={hc.id} className="card mb-2 border">
                    <div className="card-body py-2 px-3">
                      <div className="d-flex justify-content-between align-items-start mb-1">
                        <div>
                          <span className="fw-semibold text-primary small">{hc.codigo || '—'}</span>
                          <span className="text-muted small ms-2">{formatFecha(hc.fecha_consulta)}</span>
                        </div>
                        <span className={`badge ${estadoBadge(hc.estado)} small`}>
                          {estadoLabel(hc.estado)}
                        </span>
                      </div>
                      <div className="fw-semibold">{hc.cliente_nombre || '—'}</div>
                      <small className="text-muted d-block">{hc.cliente_ci || ''}</small>
                      {hc.optometrista && (
                        <small className="text-muted d-block">
                          <i className="bi bi-person me-1"></i>{hc.optometrista}
                        </small>
                      )}
                      {hc.motivo_consulta && (
                        <small className="text-muted d-block mt-1">
                          {truncate(hc.motivo_consulta, 50)}
                        </small>
                      )}
                      <div className="mt-2 d-flex gap-2">
                        <button
                          className="btn btn-outline-primary btn-sm flex-fill"
                          onClick={() => navigate(`/historia-clinica/${hc.id}`)}
                        >
                          <i className="bi bi-eye me-1"></i>Ver
                        </button>
                        <button
                          className="btn btn-outline-secondary btn-sm flex-fill"
                          onClick={() => navigate(`/historia-clinica/${hc.id}`)}
                        >
                          <i className="bi bi-pencil me-1"></i>Editar
                        </button>
                        <button
                          className="btn btn-outline-success btn-sm flex-fill"
                          onClick={() => navigate(`/historia-clinica/nueva?cliente=${hc.cliente_id}`)}
                        >
                          <i className="bi bi-plus-lg me-1"></i>Nueva HC
                        </button>
                        <button
                          className="btn btn-outline-danger btn-sm"
                          onClick={() => openExportModal(hc.cliente_id)}
                          title="Exportar PDF"
                        >
                          <i className="bi bi-file-earmark-pdf"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Pagination */}
        {!loading && historias.length > 0 && (
          <div className="card-footer bg-white border-top">
            <div className="d-flex flex-column flex-sm-row justify-content-between align-items-center gap-2">
              <small className="text-muted">
                Mostrando {startItem}-{endItem} de {totalItems}
              </small>
              <nav>
                <ul className="pagination pagination-sm mb-0">
                  <li className={`page-item ${page <= 1 ? 'disabled' : ''}`}>
                    <button
                      className="page-link"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page <= 1}
                    >
                      <i className="bi bi-chevron-left"></i>
                    </button>
                  </li>
                  {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (page <= 3) {
                      pageNum = i + 1;
                    } else if (page >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = page - 2 + i;
                    }
                    return (
                      <li key={pageNum} className={`page-item ${page === pageNum ? 'active' : ''}`}>
                        <button
                          className="page-link"
                          onClick={() => setPage(pageNum)}
                        >
                          {pageNum}
                        </button>
                      </li>
                    );
                  })}
                  <li className={`page-item ${page >= totalPages ? 'disabled' : ''}`}>
                    <button
                      className="page-link"
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page >= totalPages}
                    >
                      <i className="bi bi-chevron-right"></i>
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          </div>
        )}
      </div>

      {/* ─── Export PDF Modal ─── */}
      {showExportModal && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header bg-danger bg-opacity-10 border-danger border-opacity-25">
                <h5 className="modal-title">
                  <i className="bi bi-file-earmark-pdf text-danger me-2"></i>
                  Exportar Ficha del Paciente
                </h5>
                <button type="button" className="btn-close" onClick={closeExportModal} disabled={exporting}></button>
              </div>
              <div className="modal-body">
                {!exportCliente ? (
                  <>
                    <p className="text-muted mb-3">Seleccione un paciente para exportar sus historias cl&iacute;nicas:</p>
                    {getUniquePacientes().length === 0 ? (
                      <div className="text-center py-4 text-muted">
                        <i className="bi bi-people" style={{ fontSize: '2rem' }}></i>
                        <p className="mt-2">No hay pacientes disponibles en el listado actual.</p>
                      </div>
                    ) : (
                      <div className="list-group">
                        {getUniquePacientes().map(pac => (
                          <button
                            key={pac.id}
                            className="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                            onClick={() => handleSelectPaciente(pac)}
                          >
                            <div>
                              <div className="fw-semibold">{pac.nombre}</div>
                              {pac.ci && <small className="text-muted">CI: {pac.ci}</small>}
                            </div>
                            <i className="bi bi-chevron-right text-muted"></i>
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div className="d-flex align-items-center mb-3">
                      <button
                        className="btn btn-sm btn-outline-secondary me-3"
                        onClick={() => { setExportCliente(null); setExportHCs([]); setSelectedHCIds(new Set()); }}
                        disabled={exporting}
                      >
                        <i className="bi bi-arrow-left me-1"></i>Volver
                      </button>
                      <div>
                        <span className="fw-semibold">{exportCliente.nombre}</span>
                        {exportCliente.ci && <small className="text-muted ms-2">CI: {exportCliente.ci}</small>}
                      </div>
                    </div>

                    {loadingExportHCs ? (
                      <div className="text-center py-4">
                        <div className="spinner-border spinner-border-sm text-primary" role="status">
                          <span className="visually-hidden">Cargando...</span>
                        </div>
                        <p className="text-muted mt-2 small">Cargando historias cl&iacute;nicas...</p>
                      </div>
                    ) : exportHCs.length === 0 ? (
                      <div className="text-center py-4 text-muted">
                        <i className="bi bi-journal-x" style={{ fontSize: '2rem' }}></i>
                        <p className="mt-2">Este paciente no tiene historias cl&iacute;nicas.</p>
                      </div>
                    ) : (
                      <>
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <small className="text-muted">
                            {selectedHCIds.size} de {exportHCs.length} seleccionadas
                          </small>
                          <button
                            className="btn btn-sm btn-link text-decoration-none p-0"
                            onClick={toggleAllHCs}
                          >
                            {selectedHCIds.size === exportHCs.length ? 'Deseleccionar todas' : 'Seleccionar todas'}
                          </button>
                        </div>
                        <div className="list-group" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                          {exportHCs.map(hc => (
                            <label
                              key={hc.id}
                              className={`list-group-item list-group-item-action d-flex align-items-start gap-2 ${selectedHCIds.has(hc.id) ? 'active' : ''}`}
                              style={{ cursor: 'pointer' }}
                            >
                              <input
                                type="checkbox"
                                className="form-check-input mt-1 flex-shrink-0"
                                checked={selectedHCIds.has(hc.id)}
                                onChange={() => toggleHCSelection(hc.id)}
                              />
                              <div className="flex-grow-1">
                                <div className="d-flex justify-content-between">
                                  <span className={`fw-semibold small ${selectedHCIds.has(hc.id) ? '' : 'text-primary'}`}>
                                    {hc.codigo || `HC #${hc.id}`}
                                  </span>
                                  <span className={`badge ${estadoBadge(hc.estado)} ms-2`} style={{ fontSize: '0.7rem' }}>
                                    {estadoLabel(hc.estado)}
                                  </span>
                                </div>
                                <small className={selectedHCIds.has(hc.id) ? 'opacity-75' : 'text-muted'}>
                                  {formatFecha(hc.fecha_consulta)}
                                  {hc.motivo_consulta && ` — ${truncate(hc.motivo_consulta, 50)}`}
                                </small>
                              </div>
                            </label>
                          ))}
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>
              {exportCliente && exportHCs.length > 0 && (
                <div className="modal-footer">
                  <button className="btn btn-outline-secondary" onClick={closeExportModal} disabled={exporting}>
                    Cancelar
                  </button>
                  <button
                    className="btn btn-outline-primary"
                    onClick={() => handleExportPdf('print')}
                    disabled={exporting || selectedHCIds.size === 0}
                  >
                    {exporting ? (
                      <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                    ) : (
                      <i className="bi bi-printer me-1"></i>
                    )}
                    Imprimir
                  </button>
                  <button
                    className="btn btn-danger"
                    onClick={() => handleExportPdf('download')}
                    disabled={exporting || selectedHCIds.size === 0}
                  >
                    {exporting ? (
                      <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                    ) : (
                      <i className="bi bi-download me-1"></i>
                    )}
                    Descargar PDF
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal Cobrar Atención (integrado desde el listado de HC) */}
      <ModalCobrarAtencion
        show={showCobrarModal}
        onClose={() => setShowCobrarModal(false)}
        clientePreseleccionado={cobrarContexto.cliente}
        historiaClinicaId={cobrarContexto.hcId}
        onSuccess={() => { cargarHistorias(); }}
      />
    </div>
  );
};

export default HistoriasClinicasList;
